import {
  all, put, takeLatest, delay, race, take, fork, cancel,
} from 'redux-saga/effects';
import _ from 'lodash';
import * as Sentry from '@sentry/browser';

import { Routers } from '../config/constants';
import * as Api from '../api';
import * as ActionTypes from '../actions/auth/AuthActionsTypes';
import * as IActionTypes from '../actions/auth/IAuthActions';
import * as AuthActions from '../actions/auth/AuthActions';
import history from '../redux/history';
import { TUser } from '../types/IAuthStore';
import {
  setTokens,
  deleteTokens,
  setRememberMe,
  getRefreshToken,
  getTimeUntilTokenExpiration,
} from '../helpers/tokensHelpers';
import locales from '../locales';

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  scope: string;
  token_type: string;
}

function* setToken(email: string, password: string) {
  const response = yield Api.tokenReq(email, password);
  const token: TokenResponse = response.data;

  setTokens(token.access_token, token.refresh_token, token.expires_in);

  yield put(AuthActions.getSessionSuccess());
  yield put(AuthActions.startTokenRefreshTimer());
}

/** @description Saga для обновления токена */
function* refreshTokenSaga() {
  try {
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      // console.warn(`[${new Date().toLocaleTimeString()}] Refresh token not found`);

      yield put(AuthActions.refreshTokenFail());
      yield put(AuthActions.sessionFailed());

      return;
    }

    // console.log(`[${new Date().toLocaleTimeString()}] Обновление токена...`);
    const response = yield Api.refreshTokenReq(refreshToken);
    const token: TokenResponse = response.data;

    setTokens(token.access_token, token.refresh_token, token.expires_in);
    yield put(AuthActions.refreshTokenSuccess());
    // console.log(`[${new Date().toLocaleTimeString()}] Токен успешно обновлен`);

    yield put(AuthActions.startTokenRefreshTimer());
  } catch (e) {
    // console.error(`[${new Date().toLocaleTimeString()}] Ошибка при обновлении токена:`, e);
    yield put(AuthActions.refreshTokenFail());
    yield put(AuthActions.sessionFailed());
  }
}

/** @description Saga для автоматического обновления токена по таймеру */
function* tokenRefreshTimerSaga() {
  while (true) {
    const timeUntilExpiration = getTimeUntilTokenExpiration();
    if (!timeUntilExpiration) {
      // console.warn(`[${new Date().toLocaleTimeString()}] Время истечения токена не найдено`);
      return;
    }

    const REFRESH_BUFFER_MS = 60000;
    const delayTime = Math.max(0, timeUntilExpiration - REFRESH_BUFFER_MS);

    // console.log(`[${new Date().toLocaleTimeString()}] Токен будет обновлен через ${Math.round(delayTime / 1000)} секунд`);

    const { stopped } = yield race({
      timeout: delay(delayTime),
      stopped: take(ActionTypes.STOP_TOKEN_REFRESH_TIMER),
    });

    if (stopped) {
      // console.log(`[${new Date().toLocaleTimeString()}] Таймер обновления токена остановлен`);
      return;
    }

    yield put(AuthActions.refreshTokenRequest());
    yield take([ActionTypes.REFRESH_TOKEN_SUCCESS, ActionTypes.REFRESH_TOKEN_FAIL]);
  }
}

/** @description Регистарция юзера */
function* signUpSaga(action: IActionTypes.ISignUp) {
  try {
    yield Api.registerRestaurateurReq(action.payload);
    yield put(AuthActions.signUpSuccess());

    yield setToken(action.payload.email, action.payload.password);

    history.push(Routers.thanks);
  } catch (e) {
    if (_.has(e, 'response.status') && (e.response.status === 409)) {
      yield put(AuthActions.signUpError(locales.t('common.errors.signUpErrors.emailError')));
    } else {
      yield put(AuthActions.signUpError(locales.t('common.errors.signUpErrors.unknownError')));
    }
  }
}

/** @description Подтверждение регистрации по емейлу */
function* confirmEmailSaga(action: IActionTypes.IConfirmEmail) {
  try {
    yield Api.confirmEmailReq(action.payload.code);
    yield put(AuthActions.confirmEmailSuccess());
  } catch (e) {
    yield put(AuthActions.confirmEmailError());
  }
}

/** @description Получение нового пригласительного письма на почту */
function* confirmEmailResendSaga() {
  try {
    yield Api.confirmEmailResendReq();
  } catch (e) {
    const errorText = locales.t('common.errors.signInErrors.unknownError');
    yield put(AuthActions.signInError(errorText));
  }
}

/** @description Авторизация юзера в ЛК */
function* signInSaga(action: IActionTypes.ISignIn) {
  try {
    const { email, password, remember } = action.payload;

    setRememberMe(remember);
    yield setToken(email, password);

    const result = yield Api.getAccountReq();
    const User: TUser = result.data;

    if (User.activated) {
      yield put(AuthActions.signInSuccess(User));

      history.push(Routers.home);
    } else {
      yield put(AuthActions.signInError('common.errors.sendNewPassword.wrongEmail'));

      history.push(Routers.thanks);
    }
  } catch (e) {
    let errorText = locales.t('common.errors.signInErrors.unknownError');

    if (e.response && e.response.data.code === 'authentication_error') {
      errorText = locales.t('common.errors.signInErrors.wrongEmailOrPasswordError');
    }

    yield put(AuthActions.signInError(errorText));
  }
}

/** @description Получение сессии для авторизированного юзера */
function* getSessionSaga() {
  try {
    const result = yield Api.getAccountReq();
    const User: TUser = result.data;

    yield put(AuthActions.signInSuccess(User));

    const timeUntilExpiration = getTimeUntilTokenExpiration();
    if (timeUntilExpiration && timeUntilExpiration > 0) {
      yield put(AuthActions.startTokenRefreshTimer());
    } else {
      // console.warn(`[${new Date().toLocaleTimeString()}] Токен истек, запускаем обновление`);
      yield put(AuthActions.refreshTokenRequest());
    }
  } catch (e) {
    // console.error(`[${new Date().toLocaleTimeString()}] Ошибка getSessionSaga:`);
    // console.error(e);

    yield put(AuthActions.sessionFailed());
  }
}

/** @description Прекращение сессии для авторизированного юзера */
function* signOutSaga() {
  try {
    yield put(AuthActions.stopTokenRefreshTimer());
    deleteTokens();
    // yield Api.logoutReq();
  } catch (e) {
    console.log(e);
  }
}

function signInSuccessSaga(action: IActionTypes.ISignInSuccess) {
  const { id, email } = action.payload.user;

  Sentry.setUser({
    id: id.toString(),
    email,
  });
}


/** @description Watcher saga для управления таймером обновления токена */
function* watchTokenRefreshTimer() {
  while (true) {
    yield take(ActionTypes.START_TOKEN_REFRESH_TIMER);
    const task = yield fork(tokenRefreshTimerSaga);

    yield take([
      ActionTypes.STOP_TOKEN_REFRESH_TIMER,
      ActionTypes.SIGN_OUT,
      ActionTypes.GET_SESSION_FAIL,
    ]);

    yield cancel(task);
  }
}

export default function* saga() {
  yield all([
    takeLatest(ActionTypes.SIGN_IN_REQUEST, signInSaga),
    takeLatest(ActionTypes.SIGN_UP_REQUEST, signUpSaga),
    takeLatest(ActionTypes.GET_SESSION_REQUEST, getSessionSaga),
    takeLatest(ActionTypes.SIGN_OUT, signOutSaga),
    takeLatest(ActionTypes.CONFIRM_EMAIL_REQUEST, confirmEmailSaga),
    takeLatest(ActionTypes.CONFIRM_EMAIL_RESEND, confirmEmailResendSaga),
    takeLatest(ActionTypes.REFRESH_TOKEN_REQUEST, refreshTokenSaga),
    takeLatest(ActionTypes.SIGN_IN_SUCCESS, signInSuccessSaga),
    fork(watchTokenRefreshTimer),
  ]);
}
